import { useState, useRef, useCallback } from 'react';
import { PAGE_SIZES } from '../extensions/pagination';

export const DEFAULT_PAGE_SETTINGS = {
  enabled: true,
  format: 'A4',
  orientation: 'portrait',
  margins: { top: '2', right: '2', bottom: '2', left: '2' },
  showPageNumbers: true,
  pageNumberPosition: 'bottom-right',
  header: { enabled: false, content: '' },
  footer: { enabled: false, content: '' },
};

const SIZE_MAP = {
  A4: PAGE_SIZES.A4,
  Letter: PAGE_SIZES.LETTER,
  Legal: PAGE_SIZES.LEGAL,
};

const cmToPx = (cm) => Math.round(parseFloat(cm || '2') * 37.8);

/**
 * Converts stored page settings (format name, cm margins, etc.)
 * into PaginationPlus extension options (pixel-based).
 */
export function toPaginationOptions(settings) {
  const size = SIZE_MAP[settings.format] || PAGE_SIZES.A4;
  const isLandscape = settings.orientation === 'landscape';

  const mTop = cmToPx(settings.margins?.top);
  const mBottom = cmToPx(settings.margins?.bottom);
  const mLeft = cmToPx(settings.margins?.left);
  const mRight = cmToPx(settings.margins?.right);

  // Map pageNumberPosition to footer left/right
  let footerLeft = '';
  let footerRight = '';
  let headerLeft = '';
  let headerRight = '';

  // Header content
  if (settings.header?.enabled && settings.header?.content) {
    headerLeft = settings.header.content;
  }

  // Footer content
  if (settings.footer?.enabled && settings.footer?.content) {
    footerLeft = settings.footer.content;
  }

  // Page numbers — placed according to pageNumberPosition
  if (settings.showPageNumbers) {
    const pos = settings.pageNumberPosition || 'bottom-right';
    const isTop = pos.startsWith('top');
    const align = pos.split('-')[1];

    if (isTop) {
      if (align === 'left') headerLeft = headerLeft || '{page}';
      else if (align === 'center') headerLeft = headerLeft || '{page}';
      else headerRight = '{page}';
    } else {
      if (align === 'left') footerLeft = footerLeft || '{page}';
      else if (align === 'center') footerLeft = footerLeft || '{page}';
      else footerRight = '{page}';
    }
  }

  return {
    pageHeight: isLandscape ? size.pageWidth : size.pageHeight,
    pageWidth: isLandscape ? size.pageHeight : size.pageWidth,
    marginTop: mTop,
    marginBottom: mBottom,
    marginLeft: mLeft,
    marginRight: mRight,
    pageGap: 50,
    pageBreakBackground: '#ffffff',
    pageGapBorderColor: '#e5e5e5',
    pageGapBorderSize: 1,
    contentMarginTop: 10,
    contentMarginBottom: 10,
    headerLeft,
    headerRight,
    footerLeft,
    footerRight,
    customHeader: {},
    customFooter: {},
  };
}

/**
 * usePageSettings — manages per-contract page format preferences.
 * Reads initial state from contract.settings.page; provides a ref
 * that save callbacks can read without stale closures.
 */
export function usePageSettings({ contract }) {
  const [pageSettings, setPageSettingsState] = useState(() => {
    const saved = contract?.settings?.page;
    if (saved && typeof saved === 'object') {
      return { ...DEFAULT_PAGE_SETTINGS, ...saved };
    }
    return DEFAULT_PAGE_SETTINGS;
  });

  // Always-current ref — read by useAutoSave without stale closure
  const pageSettingsRef = useRef(pageSettings);

  const setPageSettings = useCallback((newSettings) => {
    pageSettingsRef.current = newSettings;
    setPageSettingsState(newSettings);
  }, []);

  // Merge partial updates (e.g. header content change on blur)
  const updatePageSettings = useCallback((patch) => {
    setPageSettings({ ...pageSettingsRef.current, ...patch });
  }, [setPageSettings]);

  return { pageSettings, setPageSettings, updatePageSettings, pageSettingsRef };
}
